import { Suspense, useEffect, useMemo } from 'react';
import { theme } from '@/analyses/_shared/theme';
import {
  ANALYSIS_REGISTRY,
  COUNTRY_BY_NAME,
  type CountryMeta,
} from '@/data/countries';
import { TIER_CONFIG, type Tier } from '@/data/tiers';
import { getRegionsForCountry, REGIONS, type Region } from '@/data/regions';

export type CountryPanelProps = {
  countryName: string | null;
  onClose: () => void;
  onOpenAnalysis: (countryName: string) => void;
  onAddToCompare: (countryName: string) => void;
};

/**
 * A unified selection — either a country or a region — so this panel can
 * render both kinds of analyses without duplicating the chrome.
 */
type Selection = {
  kind: 'country' | 'region';
  name: string;
  /** ISO2 for country, region id for region ("EU", "NORDICS", ...). */
  code: string;
  tier: Tier;
  summary?: string;
  lastUpdated?: string;
  analysisKey?: string;
  /** Only set for countries — drives "Member of" chips. */
  country?: CountryMeta;
  /** Only set for regions — drives the member-country list. */
  region?: Region;
};

function resolveSelection(rawName: string | null): Selection | null {
  if (!rawName) return null;
  const norm = rawName.trim().toLowerCase();

  // 1. Country match (primary)
  const c = COUNTRY_BY_NAME[norm];
  if (c) {
    return {
      kind: 'country',
      name: c.name,
      code: c.iso2,
      tier: c.tier,
      summary: c.summary,
      lastUpdated: c.lastUpdated,
      analysisKey: c.analysisKey,
      country: c,
    };
  }

  // 2. Region match (EU, Nordics, etc.) — by display name OR id
  const r = Object.values(REGIONS).find(
    (reg) =>
      reg.name.toLowerCase() === norm ||
      reg.id.toLowerCase() === norm ||
      reg.name.toLowerCase().includes(norm),
  );
  if (r) {
    return {
      kind: 'region',
      name: r.name,
      code: r.id,
      // Regions without an explicit tier fall back to "not-analyzed" for the pill.
      tier: r.tier ?? 'not-analyzed',
      summary: r.summary,
      lastUpdated: r.lastUpdated,
      analysisKey: r.analysisKey,
      region: r,
    };
  }

  return null;
}

export const CountryPanel = ({
  countryName,
  onClose,
  onOpenAnalysis,
  onAddToCompare,
}: CountryPanelProps) => {
  const selection = useMemo(() => resolveSelection(countryName), [countryName]);

  const regions = useMemo(
    () =>
      selection?.kind === 'country' && selection.country
        ? getRegionsForCountry(selection.country.iso2)
        : [],
    [selection],
  );

  const AnalysisComp = useMemo(() => {
    if (!selection?.analysisKey) return null;
    return ANALYSIS_REGISTRY[selection.analysisKey] ?? null;
  }, [selection?.analysisKey]);

  // Close on Esc
  useEffect(() => {
    if (!countryName) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [countryName, onClose]);

  if (!countryName) return null;

  const tierCfg = TIER_CONFIG[selection?.tier ?? 'not-analyzed'];
  const displayName = selection?.name ?? countryName;
  const displayCode = selection?.code ?? '';
  const isRegion = selection?.kind === 'region';

  return (
    <>
      {/* Backdrop — click to close */}
      <div
        className="no-print"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(5, 8, 20, 0.55)',
          backdropFilter: 'blur(2px)',
          zIndex: 99,
          cursor: 'pointer',
        }}
        aria-hidden
      />

      {/* Slide-in panel */}
      <div
        className="country-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${displayName} regulatory analysis`}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(860px, 95vw)',
          background: theme.DARK,
          borderLeft: `1px solid ${theme.BORDER}`,
          boxShadow: '-8px 0 40px rgba(0,0,0,0.6)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Panel header */}
        <div
          className="no-print"
          style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${theme.BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: theme.DARK2,
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minWidth: 0,
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: tierCfg.color,
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: theme.WHITE,
                  fontSize: 16,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {displayName}
                {isRegion && (
                  <span
                    style={{
                      background: theme.BLUE,
                      color: theme.WHITE,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      padding: '2px 8px',
                      borderRadius: 4,
                    }}
                  >
                    Region
                  </span>
                )}
              </div>
              <div style={{ color: theme.MUTED, fontSize: 12 }}>
                {displayCode} · {tierCfg.label}
                {selection?.lastUpdated && ` · Updated ${selection.lastUpdated}`}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {selection?.kind === 'country' && selection.tier !== 'not-analyzed' && (
              <button
                onClick={() => onAddToCompare(selection.name)}
                style={{
                  background: 'transparent',
                  color: theme.MUTED,
                  border: `1px solid ${theme.BORDER}`,
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
                title="Add to compare bar"
              >
                + Compare
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close panel"
              title="Close (Esc)"
              style={{
                background: theme.CARD,
                color: theme.WHITE,
                border: `1px solid ${theme.BORDER}`,
                borderRadius: 8,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 20,
                fontFamily: 'inherit',
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Panel body */}
        <div
          className="country-panel-body"
          style={{ flex: 1, overflowY: 'auto' }}
        >
          {AnalysisComp ? (
            <Suspense
              fallback={
                <div
                  style={{
                    padding: 40,
                    color: theme.MUTED,
                    textAlign: 'center',
                  }}
                >
                  Loading analysis…
                </div>
              }
            >
              <AnalysisComp />
            </Suspense>
          ) : (
            <div
              style={{
                padding: 40,
                color: theme.TEXT,
                fontSize: 14,
                lineHeight: 1.7,
              }}
            >
              <div
                style={{
                  background: theme.CARD,
                  borderRadius: 10,
                  border: `1px solid ${theme.BORDER}`,
                  padding: 24,
                }}
              >
                <div
                  style={{
                    color: tierCfg.color,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 6,
                  }}
                >
                  {tierCfg.label}
                </div>
                <div
                  style={{
                    color: theme.WHITE,
                    fontSize: 20,
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {displayName}
                </div>
                <div style={{ color: theme.MUTED, fontSize: 13 }}>
                  {selection?.summary ??
                    (isRegion
                      ? 'No regional analysis on file yet.'
                      : 'No regulatory analysis on file yet. Upload a JSX for this country and add its entry in src/data/countries.ts.')}
                </div>

                {isRegion && selection?.region && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 12,
                      borderTop: `1px solid ${theme.BORDER}`,
                    }}
                  >
                    <div
                      style={{
                        color: theme.MUTED,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}
                    >
                      {selection.region.memberCountries.length} member countries
                    </div>
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
                    >
                      {selection.region.memberCountries.map((iso) => {
                        // cheap lookup — not perf-critical (≤ 30 members)
                        const cm = Object.values(COUNTRY_BY_NAME).find(
                          (c) => c.iso2 === iso,
                        );
                        if (!cm) return null;
                        return (
                          <button
                            key={iso}
                            onClick={() => onOpenAnalysis(cm.name)}
                            style={{
                              background: 'transparent',
                              border: `1px solid ${theme.BORDER}`,
                              color: theme.WHITE,
                              borderRadius: 6,
                              padding: '4px 10px',
                              fontSize: 12,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                background: TIER_CONFIG[cm.tier].color,
                              }}
                            />
                            {cm.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!isRegion && regions.length > 0 && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 12,
                      borderTop: `1px solid ${theme.BORDER}`,
                    }}
                  >
                    <div
                      style={{
                        color: theme.MUTED,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}
                    >
                      Member of
                    </div>
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
                    >
                      {regions.map((r) => (
                        <button
                          key={r.id}
                          onClick={() =>
                            r.analysisKey && onOpenAnalysis(r.name)
                          }
                          disabled={!r.analysisKey}
                          style={{
                            background: 'transparent',
                            border: `1px solid ${theme.BORDER}`,
                            color: r.analysisKey
                              ? theme.WHITE
                              : theme.MUTED,
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 12,
                            cursor: r.analysisKey ? 'pointer' : 'default',
                            fontFamily: 'inherit',
                          }}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
